import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Icon, Popup, Button, Header } from 'semantic-ui-react';
import { format } from 'date-fns';
import * as portfolioApi from '~/api/portfolios';
import * as projectApi from '~/api/projects';
import { ProjectBidSummary } from '~/types/project';
import { Unit, ContractType, UnitDecimals } from '~/constants';
import { formatNumber, isPerYearOfftake } from '~/utils';
import { getProjectPvSizeKwdc, getProjectStorageSizeKwh } from '~/utils/calculators/system';
import { normalizeToOverall, normalizeToPerWdc, normalizeToOverallStorage, normalizeToPerKwhStorage } from '~/utils/calculators/costsFns';
import { usePermissions } from '~/requests/permissions/usePermissions';
import Actions from './Actions';
import BudgetTable from '~/features/projectReview/components/BudgetTable';
import { ProjectBid } from '~/types/project/ProjectBid';
import RfiResponseList from '~/features/requestForInformation/developer/RfiResponseList';
import { useProjectContext } from '~/hooks';
import { InvestorActivityChecklistSingle } from '~/features/portfolioBidding/developer/InvestorActivityChecklist';
import '../styles.scss';

interface ProjectBidCardsProps {
    sortedBidData: ProjectBidSummary[];
    budgetDisplayMode: 'unit' | 'total';
}

const ProjectBidCards: React.FC<ProjectBidCardsProps> = ({ sortedBidData, budgetDisplayMode }) => {
    const { permissions } = usePermissions();
    const { project, projectDisplayValues } = useProjectContext();
    const [hasSummaryDoc, setHasSummaryDoc] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        if (!project?.id) return;
        const id = project.portfolioId ? project.portfolioId : (project.parentId ?? project.id);
        const fetchSummary = project.portfolioId
            ? () => portfolioApi.getPortfolioSummaryDownloadUrl(id)
            : () => projectApi.getProjectSummaryDownloadUrl(id);
        fetchSummary()
            .then((data: { presignedUrl?: string }) => setHasSummaryDoc(!!data?.presignedUrl))
            .catch(() => setHasSummaryDoc(false));
    }, [project?.id, project?.parentId, project?.portfolioId]);
    const totalPvSizeKwdc = getProjectPvSizeKwdc(project);
    const totalStorageSizeKwh = getProjectStorageSizeKwh(project);
    const isStorageOnly = totalPvSizeKwdc === 0 && totalStorageSizeKwh > 0;

    // Carousel state and refs
    const carouselRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    // Check scroll position to show/hide navigation buttons
    const checkScrollPosition = () => {
        if (carouselRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
        }
    };

    // Scroll the carousel
    const scrollCarousel = (direction: 'left' | 'right') => {
        if (carouselRef.current) {
            const cardWidth = 354; // Match max card width
            const scrollAmount = cardWidth + 8; // Card width + gap
            const currentScroll = carouselRef.current.scrollLeft;
            const targetScroll = direction === 'left'
                ? currentScroll - scrollAmount
                : currentScroll + scrollAmount;

            carouselRef.current.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
            });
        }
    };

    // Update scroll position when component mounts or data changes
    useEffect(() => {
        checkScrollPosition();
        const handleResize = () => checkScrollPosition();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [sortedBidData]);

    // Add scroll event listener to carousel
    useEffect(() => {
        const carousel = carouselRef.current;
        if (carousel) {
            carousel.addEventListener('scroll', checkScrollPosition);
            return () => carousel.removeEventListener('scroll', checkScrollPosition);
        }
    }, []);

    // Helper function to convert bid amounts for display
    const convertBidAmount = (bid: ProjectBid, targetUnit: 'TOTAL' | 'WDC' | 'KWH' | 'BESS_KWH' | 'YEAR') => {
        if (!bid?.amount) return 0;

        if (isStorageOnly) {
            if (targetUnit === 'TOTAL') {
                return bid.unit === Unit.KWH
                    ? normalizeToOverallStorage(totalStorageSizeKwh, bid)
                    : bid.amount;
            } else {
                return bid.unit === Unit.KWH
                    ? bid.amount
                    : normalizeToPerKwhStorage(totalStorageSizeKwh, bid);
            }
        } else {
            if (!totalPvSizeKwdc) return 0;
            if (project.pricingType === 'OFFTAKE') {
                return bid.amount;
            }
            if (targetUnit === 'TOTAL') {
                return bid.unit === Unit.WDC
                    ? normalizeToOverall(totalPvSizeKwdc, bid)
                    : bid.amount;
            } else {
                return bid.unit === Unit.WDC
                    ? bid.amount
                    : normalizeToPerWdc(totalPvSizeKwdc, bid);
            }
        }
    };

    // Helper function to format bid amounts for display
    const formatBidAmount = (bid: ProjectBid | null, displayUnit: 'TOTAL' | 'WDC' | 'KWH' | 'BESS_KWH' | 'YEAR') => {
        if (!bid?.amount) {
            return 'Awaiting bid...';
        }

        if (!totalPvSizeKwdc && !isStorageOnly) {
            return <i style={{ color: '#5a5a5a' }}>-</i>;
        }

        const convertedAmount = convertBidAmount(bid, displayUnit);

        if (displayUnit === 'BESS_KWH') {
            return `$${formatNumber(convertedAmount, 2)}`;
        }
        if (displayUnit === 'KWH') {
            return `$${formatNumber(convertedAmount, 4)}`;
        }
        if (displayUnit === 'YEAR') {
            return `$${formatNumber(convertedAmount, UnitDecimals.YEAR)}`;
        }
        if (displayUnit === 'WDC') {
            return `$${formatNumber(convertedAmount, 3)}`;
        }
        // TOTAL
        return `$${formatNumber(convertedAmount, 0)}`;
    };

    const formatLastUpdated = (data: ProjectBidSummary) => {
        const isDeclined = data.projectStatus === 'DECLINED_TO_BID' || data.projectStatus === 'BID_REJECTED';
        const date = data.bid?.createdAt ?? (isDeclined ? data.projectUpdatedAt : null);
        if (!date) {
            return <i style={{ color: '#5a5a5a' }}>—</i>;
        }
        return format(new Date(date), 'MMM d, yyyy');
    };

    const getInvestorName = (investorBidData: any) => {
        const fullName = investorBidData.investor?.company?.name || 'Unknown Investor';
        return fullName.length > 28 ? fullName.substring(0, 28) + '...' : fullName;
    };



    const renderBidValue = (bidData: ProjectBid | null, projectStatus?: string) => {
        const isDeclined = projectStatus === 'DECLINED_TO_BID' || projectStatus === 'BID_REJECTED';
        const emptyLabel = isDeclined ? 'Declined' : 'Awaiting bid...';

        return (
            <div className="bid-value-section">
                {project?.pricingType === 'OFFTAKE' ? (
                    <div className="bid-amount">
                        <span className="amount">{bidData?.amount ? formatBidAmount(bidData, isPerYearOfftake(project?.contractType) ? 'YEAR' : 'KWH') : emptyLabel}</span>
                        <span className="unit">{bidData?.amount ? (project?.contractType === ContractType.PPA ? '/kWh' : '/year') : ''}</span>
                    </div>
                ) : budgetDisplayMode === 'unit' ? (
                    <div className="bid-amount primary">
                        <span className="amount">{bidData?.amount ? (
                            isStorageOnly ? formatBidAmount(bidData, 'BESS_KWH') + '/kWh' : formatBidAmount(bidData, 'WDC') + '/Wdc')
                            : emptyLabel}
                        </span>
                        {bidData?.amount ? (
                            <span className="unit"></span>
                        ) : null}
                    </div>
                ) : (
                    <div className="bid-amount primary">
                        <span className="amount">{bidData?.amount ? formatBidAmount(bidData, 'TOTAL') : emptyLabel}</span>
                    </div>
                )}

            </div>
        );
    };

    return (
        <div className="bid-cards-carousel">
            {/* Carousel Navigation */}
            {sortedBidData.length > 1 && (
                <>
                    <Button
                        className={`carousel-nav carousel-nav-left ${!canScrollLeft ? 'disabled' : ''}`}
                        icon="chevron left"
                        onClick={() => scrollCarousel('left')}
                        disabled={!canScrollLeft}
                    />
                    <Button
                        className={`carousel-nav carousel-nav-right ${!canScrollRight ? 'disabled' : ''}`}
                        icon="chevron right"
                        onClick={() => scrollCarousel('right')}
                        disabled={!canScrollRight}
                    />
                </>
            )}

            {/* Carousel Container */}
            <div
                ref={carouselRef}
                className="bid-cards-container"
                onScroll={checkScrollPosition}
            >
                {sortedBidData?.map((data: ProjectBidSummary, index: number) => {
                    const bidData = data.bid as ProjectBid;
                    const awaitingBid = !bidData?.amount;
                    const cardId = `${data.investor.id}-${index}`;
                    const hasRfis = data.rfiResponses && data.rfiResponses.length > 0;
                    const isDeclined = data.projectStatus === 'DECLINED_TO_BID' || data.projectStatus === 'BID_REJECTED';

                    return (
                        <Card key={cardId} className={`project-bid-card${isDeclined ? ' is-declined' : ''}`}>
                            {/* Investor Header */}
                            <Card.Content className="investor-header">
                                <div className="investor-info">
                                    <div className="investor-logo-container">
                                        {data?.investor?.company?.logoS3Url ? (
                                            <img
                                                src={data.investor.company.logoS3Url}
                                                alt={`${data.investor.company.name} logo`}
                                                className="investor-logo"
                                            />
                                        ) : (
                                            <div className="placeholder-logo"></div>
                                        )}
                                    </div>
                                    <div className="investor-details">
                                        <div className="investor-name">
                                            {getInvestorName(data)}
                                            {permissions?.isAdmin && data.investor?.company?.id ? (
                                                <Link
                                                    to={`/company-profile/${data.investor.company.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    title="Open company profile in new tab"
                                                    style={{ marginLeft: 8, fontSize: 12, textDecoration: 'none' }}
                                                >
                                                    <Icon name="external" />
                                                </Link>
                                            ) : null}
                                        </div>
                                        <div className="last-updated">Updated: {formatLastUpdated(data)}</div>
                                    </div>
                                </div>
                            </Card.Content>

                            {/* Bid Value Section */}
                            <Card.Content className="bid-content">
                                {renderBidValue(bidData, data.projectStatus)}
                                <Actions data={data} awaitingBid={awaitingBid} />
                            </Card.Content>

                            {/* IRR Display */}
                            {permissions?.isAdmin ? (
                                <div className="irr-display">
                                    <span className="irr-label">
                                        Estimated IRR
                                        <Popup
                                            position="bottom center"
                                            style={{ minWidth: '25rem' }}
                                            trigger={
                                                <Icon
                                                    name="info circle"
                                                    size='small'
                                                    style={{ color: 'var(--color-charcoal)', marginLeft: '4px' }}
                                                    tabIndex={0}
                                                />
                                            }
                                            content={
                                                <div style={{ maxWidth: '25rem' }}>
                                                    <strong style={{ fontSize: '1rem' }}>Estimated IRR</strong>
                                                    <p style={{ color: 'white' }}>This IRR is an estimated unlevered after-tax IRR as run through Conductor&apos;s model using a combination of project-specific information provided and Conductor&apos;s internal assumptions. The Buyer will have different assumptions, but this should be a good point of reference.</p>
                                                </div>
                                            }
                                            inverted
                                            on={['hover', 'focus']}
                                        />
                                    </span>
                                    <span className="irr-value">
                                        {(() => {
                                            const dealSummary = projectDisplayValues?.dealSummary;
                                            const projectBidData = dealSummary?.projectBidData?.find((bid: any) => bid.investorId === bidData?.investorId);

                                            if (projectBidData?.manualIrr) {
                                                return (projectBidData.manualIrr * 100).toFixed(3) + '%';
                                            } else if (projectBidData?.calculatedIrr) {
                                                return (projectBidData.calculatedIrr * 100).toFixed(3) + '%';
                                            } else {
                                                return <i style={{ color: '#5a5a5a' }}>TBD</i>;
                                            }
                                        })()}
                                    </span>
                                </div>
                            ) : null}

                            {/* Budget Table - Always shown */}
                            <Card.Content className="budget-content">
                                <BudgetTable project={project} formState={bidData} viewMode="card" budgetDisplayMode={budgetDisplayMode} />
                            </Card.Content>


                            {/* Supporting Information - Shown if RFI responses exist */}
                            {hasRfis && (
                                <Card.Content className="rfi-content">
                                    <RfiResponseList
                                        rfiResponses={data.rfiResponses || []}
                                        investorName={getInvestorName(data)}
                                        tableView={false}
                                    />
                                </Card.Content>
                            )}

                            {/* Activity checklist - hidden for developers when project is part of a portfolio */}
                            {(permissions?.isAdmin || (permissions?.isDeveloper && !project?.portfolioId)) && (
                                <div>
                                    <Header as="h4" className="card-subheading">Activity</Header>
                                    <InvestorActivityChecklistSingle
                                        context="project"
                                        activity={
                                            data.investor?.company?.id != null
                                                ? (project as any)?.investorActivityByCompanyId?.[data.investor.company.id]
                                                : undefined
                                        }
                                        showDates={permissions?.isAdmin}
                                        hasSummaryDoc={hasSummaryDoc}
                                    />
                                </div>
                            )}
                        </Card>
                    );
                })}
            </div>

            {sortedBidData?.length === 0 && (
                <div className="empty-state">
                    <Icon name="hourglass half" size="large" />
                    <h3>No bids available</h3>
                    <p>Investors haven&apos;t submitted bids yet.</p>
                </div>
            )}
        </div>
    );
};

export default ProjectBidCards;